/**
 * Briefing Rio, the way you'd brief a colleague: say what matters for the
 * role, and Rio says back what it changed and what it wouldn't, with reasons.
 * Its change is already on the card; Keep accepts it, Undo puts the card back.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import type { BriefMessage } from '../../interviewer/useInterviewer';
import { CheckIcon, SendIcon, SkipIcon } from './icons';
import styles from './index.module.css';

interface Props {
  messages: BriefMessage[];
  busy: boolean;
  disabled: boolean;
  onSend: (instruction: string) => void;
  onKeep: () => void;
  onUndo: () => void;
}

const SUGGESTIONS = [
  "We need someone who's handled on-call",
  'Skip logistics for internal candidates',
  'Keep it to 30 minutes',
  'Ask how they use AI in their work',
];

const BriefRio = ({
  messages,
  busy,
  disabled,
  onSend,
  onKeep,
  onUndo,
}: Props) => {
  const ids = useId();
  const [text, setText] = useState('');
  const thread = useRef<HTMLOListElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  // Keep the newest exchange in view.
  useEffect(() => {
    const el = thread.current;
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight });
    }
  }, [messages]);

  // Grow with the brief, up to the CSS max-height.
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    const brief = text.trim();
    if (!brief || busy || disabled) return;
    onSend(brief);
    setText('');
  };

  return (
    <section
      className={`${styles.panel} ${styles.brief}`}
      aria-labelledby={`${ids}-title`}
    >
      <div className={styles.briefHead}>
        <span className={styles.avatar} aria-hidden>
          R
        </span>
        <div>
          <h2 className={styles.briefTitle} id={`${ids}-title`}>
            Tell Rio what you&rsquo;re looking for
          </h2>
          <p className={styles.briefSub}>
            Rio changes the draft and tells you what it did.
          </p>
        </div>
      </div>

      <ol
        className={styles.thread}
        ref={thread}
        aria-live="polite"
        aria-label="Your brief to Rio"
      >
        {messages.length === 0 && (
          <li className={styles.empty}>
            <p className={styles.hint}>
              Brief Rio the way you&rsquo;d brief a colleague. For example:
            </p>
            <div className={styles.suggestions}>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={styles.suggestion}
                  disabled={disabled}
                  onClick={() => {
                    setText(s);
                    input.current?.focus();
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </li>
        )}
        {messages.map((m) =>
          m.from === 'you' ? (
            <li key={m.id} className={styles.you}>
              <span className={styles.srOnly}>You: </span>
              {m.text}
            </li>
          ) : (
            <li key={m.id} className={styles.rio}>
              <span
                className={`${styles.avatar} ${styles.rioMark}`}
                aria-hidden
              >
                R
              </span>
              <div className={styles.reply}>
                <span className={styles.srOnly}>Rio: </span>
                {m.status === 'thinking' && (
                  <span
                    className={styles.thinking}
                    role="status"
                    aria-label="Rio is working on it"
                  >
                    <span />
                    <span />
                    <span />
                  </span>
                )}
                {m.status === 'error' && (
                  <>
                    <p className={styles.replyLead}>
                      I couldn&rsquo;t make that change.
                    </p>
                    <p className={styles.hint}>{m.text}</p>
                    {m.retry && (
                      <div className={styles.decision}>
                        <button
                          type="button"
                          className={styles.textButton}
                          disabled={busy || disabled}
                          onClick={() => m.retry && onSend(m.retry)}
                        >
                          Try again
                        </button>
                      </div>
                    )}
                  </>
                )}
                {m.status === 'done' && (
                  <>
                    {(m.applied?.length ?? 0) > 0 && (
                      <>
                        <p className={styles.replyLead}>
                          Here&rsquo;s what I changed:
                        </p>
                        <ul className={styles.changes}>
                          {m.applied?.map((change, i) => (
                            <li
                              key={`${change.op}-${i}`}
                              className={`${styles.change} ${styles.rise}`}
                              style={{ animationDelay: `${i * 50}ms` }}
                            >
                              <span className={styles.changeIcon}>
                                <CheckIcon />
                              </span>
                              <span>{change.summary}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {(m.rejected?.length ?? 0) > 0 && (
                      <>
                        <p className={styles.replyLead}>
                          {(m.applied?.length ?? 0) > 0
                            ? 'I left this out:'
                            : 'I didn’t change anything:'}
                        </p>
                        <ul className={styles.changes}>
                          {m.rejected?.map((change, i) => (
                            <li
                              key={`${change.op}-${i}`}
                              className={`${styles.change} ${styles.rise}`}
                              style={{
                                animationDelay: `${((m.applied?.length ?? 0) + i) * 50}ms`,
                              }}
                            >
                              <span
                                className={`${styles.changeIcon} ${styles.changeIconMuted}`}
                              >
                                <SkipIcon />
                              </span>
                              <span>
                                {change.summary}
                                <span className={styles.reason}>
                                  {change.reason}
                                </span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {m.decision === 'pending' && (
                      <div className={styles.decision}>
                        <button
                          type="button"
                          className={styles.textButton}
                          onClick={onKeep}
                        >
                          Keep
                        </button>
                        <button
                          type="button"
                          className={`${styles.textButton} ${styles.textButtonQuiet}`}
                          onClick={onUndo}
                        >
                          Undo
                        </button>
                      </div>
                    )}
                    {m.decision === 'kept' && (
                      <span className={styles.decided}>Kept</span>
                    )}
                    {m.decision === 'undone' && (
                      <span className={styles.decided}>
                        Undone. The card is back as it was.
                      </span>
                    )}
                  </>
                )}
              </div>
            </li>
          ),
        )}
      </ol>

      <form className={styles.composer} onSubmit={submit}>
        <div className={styles.composerRow}>
          <label className={styles.srOnly} htmlFor={`${ids}-input`}>
            Brief for Rio
          </label>
          <textarea
            id={`${ids}-input`}
            ref={input}
            rows={1}
            value={text}
            placeholder="Describe who you're looking for"
            disabled={disabled}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                !e.shiftKey &&
                !e.nativeEvent.isComposing
              ) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <button
            type="submit"
            className={styles.send}
            aria-label="Send to Rio"
            disabled={busy || disabled || !text.trim()}
          >
            <SendIcon />
          </button>
        </div>
        <p className={styles.composerHint}>
          Enter to send, Shift and Enter for a new line.
        </p>
      </form>
    </section>
  );
};

export default BriefRio;
